import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Building2, Save, X } from 'lucide-react';
import { Client } from '@/types/client';
import { BasicInfoSection } from './client-form/BasicInfoSection';
import { AddressSection } from './client-form/AddressSection';
import { ContactsSection } from './client-form/ContactsSection';
import { BillingSection } from './client-form/BillingSection';
import { SystemSettingsSection } from './client-form/SystemSettingsSection';
import { StatusSection } from './client-form/StatusSection';

interface ClientFormProps {
  client: Client | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (client: Partial<Client>) => void;
  onRequestEditAsaasId?: () => void;
}

export const ClientForm: React.FC<ClientFormProps> = ({ client, isOpen, onClose, onSave, onRequestEditAsaasId }) => {
  const form = useForm<Client>({
    defaultValues: {
      company_tax_id: '',
      legal_name: '',
      trade_name: '',
      address_street: '',
      address_number: '',
      address_neighborhood: '',
      state: '',
      postal_code: '',
      phone_number: '',
      email: '',
      onboarding_contact_phone: '',
      billing_contact_phone: '',
      due_day: 10,
      monthly_fee: 0,
      billing_type: 'mensal',
      send_pix: false,
      keyword: '',
      is_active: true,
      schedule_start_time: '9',
      system_user: '',
      system_password: '',
      system_key: '',
      system_token: '',
      system_url: '',
      api_base_url: '',
      asaas_id: '',
      trello_card: '',
      system_management: '',
      account_id: undefined,
      atomos_chat_account_id: '',
      email_canal_enabled: false
    }
  });

  // Resetar o formulário quando o cliente mudar
  useEffect(() => {
    if (client) {
      console.log('ClientForm: Carregando dados do cliente para edição:', client);
      // Garantir que valores booleanos sejam tratados corretamente
      const clientData = {
        ...client,
        send_pix: client.send_pix ?? false, // Garantir que undefined/null seja false
        is_active: client.is_active ?? true, // Garantir que undefined/null seja true
        email_canal_enabled: client.email_canal_enabled ?? false, // Garantir que undefined/null seja false
      };
      form.reset(clientData);
    } else {
      console.log('ClientForm: Resetando formulário para novo cliente');
      form.reset({
        company_tax_id: '',
        legal_name: '',
        trade_name: '',
        address_street: '',
        address_number: '',
        address_neighborhood: '',
        state: '',
        postal_code: '',
        phone_number: '',
        email: '',
        onboarding_contact_phone: '',
        billing_contact_phone: '',
        due_day: 10,
        monthly_fee: 0,
        billing_type: 'mensal',
        send_pix: false,
        keyword: '',
        is_active: true,
        schedule_start_time: '9',
        system_user: '',
        system_password: '',
        system_key: '',
        system_token: '',
        system_url: '',
        api_base_url: '',
        asaas_id: '',
        trello_card: '',
        system_management: '',
        account_id: undefined,
        atomos_chat_account_id: '',
        email_canal_enabled: false
      });
    }
  }, [client, form]);

  const onSubmit = (data: Client) => {
    console.log('ClientForm: Enviando dados do formulário:', data);
    onSave(data);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border border-gray-200 text-gray-900 max-w-6xl max-h-[95vh] overflow-y-auto shadow-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center text-2xl font-montserrat font-bold text-gray-900">
            <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center mr-3">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            {client ? 'Editar Cliente' : 'Novo Cliente'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <BasicInfoSection form={form} isEditing={!!client} />
            <AddressSection form={form} />
            <ContactsSection form={form} />
            <BillingSection form={form} />
            <SystemSettingsSection
              form={form}
              onRequestEditAsaasId={onRequestEditAsaasId}
              isEditing={!!client}
              clientId={client?.id}
              companyTaxId={form.watch('company_tax_id')}
            />
            <StatusSection form={form} />

            {/* Botões */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="border-gray-200 text-gray-700 hover:bg-gray-50 font-medium"
              >
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <Save className="w-4 h-4 mr-2" />
                Salvar Cliente
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
